import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-selection',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="min-h-screen flex flex-col items-center justify-center bg-benzGrey-50 px-4 py-8">
      <div class="w-full max-w-md">
        
        @if (isCheckingAuth) {
          <!-- Loading State -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <div class="flex flex-col items-center justify-center py-12">
              <svg class="animate-spin h-12 w-12 text-benzRed-700 mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-benzGrey-600">Anmeldestatus wird überprüft...</p>
            </div>
          </div>
        } @else if (authService.isAuthenticated()) {
          <!-- Already Logged In View -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Willkommen zurück!</h1>
            <p class="text-benzGrey-600">Sie sind bereits angemeldet als</p>
            <p class="text-lg font-semibold text-benzRed-700 mt-2">{{ authService.currentUser()?.username || 'Benutzer' }}</p>
          </div>

          <div class="flex flex-col gap-4">
            <!-- Continue as logged in user -->
            <button
              type="button"
              (click)="continueAsLoggedInUser()"
              class="group relative overflow-hidden bg-benzRed-700 text-white border-2 border-benzRed-700 rounded-xl p-6 hover:bg-benzRed-800 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-benzRed-300">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-12 h-12 bg-benzRed-600 rounded-full flex items-center justify-center group-hover:bg-benzRed-700 transition-colors">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div class="flex-1 text-left">
                  <h3 class="text-lg font-semibold mb-1">Mit diesem Konto fortfahren</h3>
                  <p class="text-sm opacity-90">
                    Nutzen Sie Ihre gespeicherten Adressen und Einstellungen
                  </p>
                </div>
                <svg class="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </div>
            </button>

            <!-- Logout and continue as guest -->
            <button
              type="button"
              (click)="logoutAndContinueAsGuest()"
              class="group relative overflow-hidden bg-white border-2 border-benzGrey-300 rounded-xl p-6 hover:border-benzGrey-900 hover:bg-benzGrey-50 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-benzGrey-300">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-12 h-12 bg-benzGrey-100 rounded-full flex items-center justify-center group-hover:bg-benzGrey-200 transition-colors">
                  <svg class="w-6 h-6 text-benzGrey-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                  </svg>
                </div>
                <div class="flex-1 text-left">
                  <h3 class="text-lg font-semibold text-benzGrey-900 mb-1">Abmelden und als Gast fortfahren</h3>
                  <p class="text-sm text-benzGrey-600">
                    Bestellen Sie ohne Ihr Konto zu verwenden
                  </p>
                </div>
                <svg class="w-6 h-6 text-benzGrey-400 group-hover:text-benzGrey-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </div>
            </button>
          </div>
        } @else {
          <!-- Not Logged In View (Original) -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Willkommen</h1>
            <p class="text-benzGrey-600">Wie möchten Sie fortfahren?</p>
          </div>

          <!-- Auth Options -->
          <div class="flex flex-col gap-4">
          
          <!-- Login Option -->
          <button
            type="button"
            (click)="selectLogin()"
            class="group relative overflow-hidden bg-white border-2 border-benzGrey-900 rounded-xl p-6 hover:border-benzRed-700 hover:bg-benzRed-50 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-benzRed-300">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 bg-benzRed-100 rounded-full flex items-center justify-center group-hover:bg-benzRed-200 transition-colors">
                <svg class="w-6 h-6 text-benzRed-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <h3 class="text-lg font-semibold text-benzGrey-900 mb-1">Anmelden</h3>
                <p class="text-sm text-benzGrey-600">
                  Melden Sie sich mit Ihrem bestehenden Konto an
                </p>
              </div>
              <svg class="w-6 h-6 text-benzGrey-400 group-hover:text-benzRed-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
          </button>

          <!-- Register Option -->
          <button
            type="button"
            (click)="selectRegister()"
            class="group relative overflow-hidden bg-white border-2 border-benzGrey-900 rounded-xl p-6 hover:border-benzRed-700 hover:bg-benzRed-50 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-benzRed-300">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 bg-benzRed-100 rounded-full flex items-center justify-center group-hover:bg-benzRed-200 transition-colors">
                <svg class="w-6 h-6 text-benzRed-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <h3 class="text-lg font-semibold text-benzGrey-900 mb-1">Registrieren</h3>
                <p class="text-sm text-benzGrey-600">
                  Erstellen Sie ein neues Konto und profitieren Sie von Vorteilen
                </p>
              </div>
              <svg class="w-6 h-6 text-benzGrey-400 group-hover:text-benzRed-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
            <!-- Benefits Badge -->
            <div class="mt-3 flex flex-wrap gap-2">
              <span class="inline-block px-2 py-1 text-xs bg-benzRed-100 text-benzRed-700 rounded-md">
                Schnellerer Checkout
              </span>
              <span class="inline-block px-2 py-1 text-xs bg-benzRed-100 text-benzRed-700 rounded-md">
                Bestellübersicht
              </span>
              <span class="inline-block px-2 py-1 text-xs bg-benzRed-100 text-benzRed-700 rounded-md">
                Datenverwaltung
              </span>
            </div>
          </button>

          <!-- Guest Option -->
          <button
            type="button"
            (click)="selectGuest()"
            class="group relative overflow-hidden bg-white border-2 border-benzGrey-300 rounded-xl p-6 hover:border-benzGrey-900 hover:bg-benzGrey-50 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-benzGrey-300">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 bg-benzGrey-100 rounded-full flex items-center justify-center group-hover:bg-benzGrey-200 transition-colors">
                <svg class="w-6 h-6 text-benzGrey-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <h3 class="text-lg font-semibold text-benzGrey-900 mb-1">Als Gast fortfahren</h3>
                <p class="text-sm text-benzGrey-600">
                  Ohne Registrierung bestellen
                </p>
              </div>
              <svg class="w-6 h-6 text-benzGrey-400 group-hover:text-benzGrey-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
          </button>
          </div>
        }
        
        <!-- Info Text -->
        <div class="mt-8 text-center">
          <p class="text-sm text-benzGrey-600">
            Ihre Daten sind bei uns sicher und werden vertraulich behandelt.
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class AuthSelectionComponent implements OnInit {
  private router = inject(Router);
  public authService = inject(AuthService);
  public isCheckingAuth = true;

  ngOnInit(): void {
    // Always check auth status when this component loads
    // This handles cases where user manually navigates to /auth
    const bookingMode = sessionStorage.getItem('bookingMode');
    
    if (bookingMode === 'authenticated') {
      // User claims to be authenticated → verify with backend
      this.authService.checkUserLoggedIn().subscribe({
        next: (response) => {
          if (response.logged_in && response.user) {
            // Update auth service state
            this.authService.isAuthenticated.set(true);
            this.authService.currentUser.set(response.user);
            
            // Store/update user info in session storage
            sessionStorage.setItem('userId', response.user.id.toString());
            sessionStorage.setItem('userName', response.user.username);
            if (response.user.email) {
              sessionStorage.setItem('userEmail', response.user.email);
            }
          } else {
            // Session expired → logout
            this.authService.logout();
          }
          this.isCheckingAuth = false;
        },
        error: (error) => {
          console.error('Backend auth check failed:', error);
          // Backend check failed → logout
          this.authService.logout();
          this.isCheckingAuth = false;
        }
      });
    } else {
      // No auth claim → show normal auth selection
      this.isCheckingAuth = false;
    }
  }

  /**
   * Continue booking with the currently logged-in user account
   */
  continueAsLoggedInUser(): void {
    this.continueToBooking();
  }

  /**
   * Logout and continue booking as guest
   */
  logoutAndContinueAsGuest(): void {
    this.authService.logout();
    sessionStorage.setItem('bookingMode', 'guest');
    this.continueToBooking();
  }

  selectLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  selectRegister(): void {
    this.router.navigate(['/auth/register']);
  }

  selectGuest(): void {
    // Set guest mode flag and continue to booking
    sessionStorage.setItem('bookingMode', 'guest');
    this.continueToBooking();
  }

  private continueToBooking(): void {
    // Get the selected event slug from sessionStorage
    const eventSlug = sessionStorage.getItem('selectedEventSlug');
    
    if (eventSlug) {
      // Navigate to the booking form for the selected event
      this.router.navigate(['/booking', eventSlug]);
    } else {
      // Fallback: navigate to event selection if no event was selected
      this.router.navigate(['/']);
    }
  }
}

